import {
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { pushAnalyticsEvent } from '@/lib/analytics';
import { reconcileDonorIdentity } from '@/lib/stripe-billing';

const DONOR_CLAIM_EMAIL_STORAGE_KEY = 'gsgo_donor_claim_email';

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function buildActionCodeUrl(): string {
  const base = globalThis.location.origin;
  return `${base}/donate/thank-you?claim=complete`;
}

export async function sendDonorClaimLink(email: string): Promise<void> {
  const normalizedEmail = normalizeEmail(email);
  const actionCodeSettings = {
    url: buildActionCodeUrl(),
    handleCodeInApp: true,
  };

  await sendSignInLinkToEmail(auth, normalizedEmail, actionCodeSettings);
  globalThis.localStorage.setItem(DONOR_CLAIM_EMAIL_STORAGE_KEY, normalizedEmail);
  pushAnalyticsEvent({
    event: 'donor_claim_link_sent',
    donor_email_domain: normalizedEmail.split('@')[1] ?? 'unknown',
  });
}

function consumeStoredClaimEmail(): string | null {
  const stored = globalThis.localStorage.getItem(DONOR_CLAIM_EMAIL_STORAGE_KEY);
  if (!stored) return null;
  const normalized = normalizeEmail(stored);
  if (!normalized) return null;
  return normalized;
}

export async function completeDonorClaimIfPresent(url: string): Promise<{
  processed: boolean;
  success: boolean;
  linkedCount: number;
  message: string;
}> {
  if (!isSignInWithEmailLink(auth, url)) {
    return { processed: false, success: false, linkedCount: 0, message: 'not-claim-link' };
  }

  const email = consumeStoredClaimEmail();
  if (!email) {
    return {
      processed: true,
      success: false,
      linkedCount: 0,
      message:
        'We could not find your claim email on this device. Open the same claim link on the original device, then retry.',
    };
  }

  await signInWithEmailLink(auth, email, url);
  globalThis.localStorage.removeItem(DONOR_CLAIM_EMAIL_STORAGE_KEY);
  const result = await reconcileDonorIdentity(email);
  const linkedCount = result.donationsLinked + result.invoiceLinks + result.donorsLinked;

  pushAnalyticsEvent({
    event: 'donor_claim_completed',
    donor_links_count: linkedCount,
  });

  return {
    processed: true,
    success: true,
    linkedCount,
    message: `Your donor account has been claimed. Linked ${linkedCount} historical records.`,
  };
}
